import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import './RevenueImpactModal.css';

// Chart colors — 5 bold, maximally-distinct colors.
// Fewer colours ⇒ dash patterns kick in sooner, making lines easy to tell apart.
const CHART_COLORS = [
  '#2563EB',   // blue
  '#DC2626',   // red
  '#059669',   // green
  '#D97706',   // orange
  '#7C3AED',   // purple
];

// Dash patterns — cycled after all colours have been used once
const DASH_PATTERNS = [
  '',              // solid
  '10 5',          // long dashed
  '3 3',           // dotted
  '14 4 3 4',     // dash-dot
  '6 2',           // short dash
];

/**
 * Returns [color, dashPattern] for line index i.
 * First cycle through all colors solid, then dash all colors, etc.
 */
function getLineStyle(i) {
  const colorIdx = i % CHART_COLORS.length;
  const dashIdx = Math.floor(i / CHART_COLORS.length) % DASH_PATTERNS.length;
  return { color: CHART_COLORS[colorIdx], dash: DASH_PATTERNS[dashIdx] };
}

// Default financial parameters
const DEFAULT_PARAMS = {
  profitPerJob: 10000,
  latenessPerDay: 500,
  effortPerUnit: 10,
  minProfit: 1000,
};

const SLIDER_CONFIG = [
  { key: 'profitPerJob', label: 'Profit / Job', min: 0, max: 50000, step: 500 },
  { key: 'latenessPerDay', label: 'Lateness / Day', min: 0, max: 5000, step: 50 },
  { key: 'effortPerUnit', label: 'Effort / Unit', min: 0, max: 500, step: 1 },
  { key: 'minProfit', label: 'Min Profit', min: 0, max: 10000, step: 100 },
];

/**
 * Calculate accumulated net profit by day for one solution.
 */
function calculateAccumulated(jobs, params) {
  const { profitPerJob, latenessPerDay, effortPerUnit, minProfit } = params;
  const byDay = {};

  for (const job of jobs) {
    const revenue = profitPerJob;
    const latenessPenalty = job.lateness_days * latenessPerDay;
    const effortPenalty = job.effort_units * effortPerUnit;
    const net = Math.max(minProfit, revenue - latenessPenalty - effortPenalty);
    const day = job.completion_day;

    if (!byDay[day]) byDay[day] = { jobs: 0, net: 0 };
    byDay[day].jobs += 1;
    byDay[day].net += net;
  }

  const days = Object.keys(byDay).map(Number).sort((a, b) => a - b);
  let cum = 0;
  return days.map(day => {
    cum += byDay[day].net;
    return { day, accumulated: cum, dailyJobs: byDay[day].jobs };
  });
}

/**
 * Calculate total net profit for a solution.
 */
function calculateTotal(jobs, params) {
  const { profitPerJob, latenessPerDay, effortPerUnit, minProfit } = params;
  let total = 0;
  for (const job of jobs) {
    const net = Math.max(
      minProfit,
      profitPerJob - job.lateness_days * latenessPerDay - job.effort_units * effortPerUnit
    );
    total += net;
  }
  return total;
}

/**
 * Build unified chart data array for recharts.
 */
function buildChartData(solutions, params) {
  const seriesMap = {};
  for (const sol of solutions) {
    seriesMap[sol.id] = calculateAccumulated(sol.jobs || [], params);
  }

  // Collect all unique days
  const allDays = new Set();
  for (const series of Object.values(seriesMap)) {
    for (const point of series) allDays.add(point.day);
  }
  const sortedDays = [...allDays].sort((a, b) => a - b);

  // Build unified data
  return sortedDays.map(day => {
    const point = { day };
    for (const sol of solutions) {
      const series = seriesMap[sol.id];
      let lastAcc = 0;
      for (const p of series) {
        if (p.day <= day) lastAcc = p.accumulated;
        else break;
      }
      point[sol.id] = lastAcc;
    }
    return point;
  });
}

/**
 * Custom tooltip for the chart.
 */
function CustomTooltip({ active, payload, label, solutions, chartData }) {
  if (!active || !payload || !payload.length) return null;
  // Sort entries by value descending (highest line at that day = top of tooltip).
  // For ties at this day, compare next-day values to determine which line is
  // visually above the other (higher slope → line renders above with monotone interp).
  const sorted = [...payload].sort((a, b) => {
    const diff = (b.value || 0) - (a.value || 0);
    if (diff !== 0) return diff;
    // Tie — peek at the NEXT day in the chart data to see which line goes higher
    if (chartData && chartData.length > 0 && a.dataKey && b.dataKey) {
      const idx = chartData.findIndex(d => d.day === label);
      if (idx >= 0) {
        // Check next day first, then previous day
        const next = chartData[idx + 1];
        if (next) {
          const nA = next[a.dataKey] ?? 0;
          const nB = next[b.dataKey] ?? 0;
          if (nB !== nA) return nB - nA;
        }
        const prev = chartData[idx - 1];
        if (prev) {
          const pA = prev[a.dataKey] ?? 0;
          const pB = prev[b.dataKey] ?? 0;
          if (pA !== pB) return pB - pA;  // higher prev = approaching from above
        }
      }
    }
    return 0;
  });
  return (
    <div className="ri-custom-tooltip">
      <div className="ri-tooltip-day">Day {label}</div>
      {sorted.map((entry, i) => {
        const sol = solutions.find(s => s.id === entry.dataKey);
        return (
          <div key={i} className="ri-tooltip-entry" style={{ color: entry.color }}>
            {sol ? sol.label : entry.name}: ${entry.value?.toLocaleString()}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Format number as currency string.
 */
function formatCurrency(val) {
  if (Math.abs(val) >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (Math.abs(val) >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

/**
 * RevenueImpactModal — Full-screen modal for revenue impact comparison.
 *
 * Props:
 *   onClose: () => void
 *   csvFilenames: { [wc: string]: string } — CSV filenames from last optimization
 */
const RevenueImpactModal = ({ onClose, csvFilenames, capacityData, scheduleStartDate }) => {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useState({ ...DEFAULT_PARAMS });
  const [editingLegendId, setEditingLegendId] = useState(null);

  // Legend right-click context menu state
  const [legendContextMenu, setLegendContextMenu] = useState(null); // { solId, x, y }

  // Close context menu on any click outside
  useEffect(() => {
    if (!legendContextMenu) return;
    const close = () => setLegendContextMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [legendContextMenu]);

  // Add Solution picker state
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [addingId, setAddingId] = useState(null);

  // Manual schedule upload state
  const [uploadingManual, setUploadingManual] = useState(false);
  const manualFileInputRef = useRef(null);

  // Night runs picker state
  const [showNightRunsPicker, setShowNightRunsPicker] = useState(false);
  const [nightRunsDates, setNightRunsDates] = useState([]);
  const [loadingNightRuns, setLoadingNightRuns] = useState(false);
  const [addingNightRuns, setAddingNightRuns] = useState({});

  const apiBaseUrl = process.env.REACT_APP_API_URL || '';

  // Compute initial data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Build CSV filenames array from the csvFiles map
        const filenames = Object.values(csvFilenames || {}).map(p =>
          p.includes('/') ? p.split('/').pop() : p
        );

        if (filenames.length === 0) {
          setError('No optimization result files available.');
          setLoading(false);
          return;
        }

        const response = await fetch(`${apiBaseUrl}/api/revenue-impact/compute`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            solutions: [{
              id: 'optimized',
              label: 'Optimized',
              csv_filenames: filenames
            }],
            include_manual: true,
            capacityData: capacityData || undefined,
            scheduleStartDate: scheduleStartDate || undefined,
          })
        });

        const result = await response.json();
        if (!result.success) {
          setError(result.error || 'Computation failed');
        } else {
          setSolutions(result.solutions || []);
        }
      } catch (err) {
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [csvFilenames, apiBaseUrl]);

  // Build chart data (recomputed whenever solutions or params change)
  const chartData = useMemo(() =>
    buildChartData(solutions, params),
    [solutions, params]
  );

  // Compute totals for each solution
  const solutionTotals = useMemo(() => {
    const map = {};
    for (const sol of solutions) {
      map[sol.id] = calculateTotal(sol.jobs || [], params);
    }
    return map;
  }, [solutions, params]);

  // Slider change handler
  const handleParamChange = useCallback((key, value) => {
    setParams(prev => ({ ...prev, [key]: Number(value) }));
  }, []);

  // Reset sliders to defaults
  const handleReset = useCallback(() => {
    setParams({ ...DEFAULT_PARAMS });
  }, []);

  // Update solution label
  const handleLabelChange = useCallback((solId, newLabel) => {
    setSolutions(prev =>
      prev.map(s => s.id === solId ? { ...s, label: newLabel } : s)
    );
  }, []);

  // Remove a solution
  const handleRemoveSolution = useCallback((solId) => {
    setSolutions(prev => prev.filter(s => s.id !== solId));
  }, []);

  // Open Add Solution picker
  const handleOpenAddPicker = useCallback(async () => {
    setShowAddPicker(true);
    setLoadingGroups(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/revenue-impact/available-results`);
      const data = await res.json();
      setAvailableGroups(data.groups || []);
    } catch (err) {
      console.error('Failed to load available results:', err);
    } finally {
      setLoadingGroups(false);
    }
  }, [apiBaseUrl]);

  // Add a solution from a result group
  const handleAddSolution = useCallback(async (group) => {
    setAddingId(group.timestamp);
    try {
      const response = await fetch(`${apiBaseUrl}/api/revenue-impact/compute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          solutions: [{
            id: `run_${group.timestamp}`,
            label: `Run ${group.timestamp.substring(0, 10)}`,
            csv_filenames: group.files
          }],
          include_manual: false,
          capacityData: capacityData || undefined,
          scheduleStartDate: scheduleStartDate || undefined,
        })
      });

      const result = await response.json();
      if (result.success && result.solutions && result.solutions.length > 0) {
        setSolutions(prev => [...prev, ...result.solutions]);
      }
    } catch (err) {
      console.error('Failed to add solution:', err);
    } finally {
      setAddingId(null);
      setShowAddPicker(false);
    }
  }, [apiBaseUrl]);

  // Open Night Runs picker
  const handleOpenNightRunsPicker = useCallback(async () => {
    setShowNightRunsPicker(true);
    setLoadingNightRuns(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/revenue-impact/available-nightruns`);
      const data = await res.json();
      setNightRunsDates(data.dates || []);
    } catch (err) {
      console.error('Failed to load night runs:', err);
    } finally {
      setLoadingNightRuns(false);
    }
  }, [apiBaseUrl]);

  // Load all runs from a night-run date directory
  const handleLoadNightRunDate = useCallback(async (dateEntry) => {
    setAddingNightRuns(prev => ({ ...prev, [dateEntry.date]: true }));
    try {
      // Build solutions array — one per run subdirectory
      const nightSolutions = dateEntry.runs.map(run => ({
        id: `nightrun_${dateEntry.date}_${run.name}`,
        label: run.description || run.name,
        csv_filenames: run.csvFiles, // absolute paths — backend resolves them
      }));

      const response = await fetch(`${apiBaseUrl}/api/revenue-impact/compute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          solutions: nightSolutions,
          include_manual: false,
          capacityData: capacityData || undefined,
          scheduleStartDate: scheduleStartDate || undefined,
        })
      });

      const result = await response.json();
      if (result.success && result.solutions && result.solutions.length > 0) {
        setSolutions(prev => [...prev, ...result.solutions]);
      } else {
        alert(result.error || 'Failed to load night run data');
      }
    } catch (err) {
      alert(`Failed to load night runs: ${err.message}`);
    } finally {
      setAddingNightRuns(prev => ({ ...prev, [dateEntry.date]: false }));
      setShowNightRunsPicker(false);
    }
  }, [apiBaseUrl, capacityData, scheduleStartDate]);

  // Load a single run from a night-run
  const handleLoadSingleNightRun = useCallback(async (dateEntry, run) => {
    const key = `${dateEntry.date}_${run.name}`;
    setAddingNightRuns(prev => ({ ...prev, [key]: true }));
    try {
      const response = await fetch(`${apiBaseUrl}/api/revenue-impact/compute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          solutions: [{
            id: `nightrun_${dateEntry.date}_${run.name}`,
            label: run.description || run.name,
            csv_filenames: run.csvFiles,
          }],
          include_manual: false,
          capacityData: capacityData || undefined,
          scheduleStartDate: scheduleStartDate || undefined,
        })
      });

      const result = await response.json();
      if (result.success && result.solutions && result.solutions.length > 0) {
        setSolutions(prev => [...prev, ...result.solutions]);
      } else {
        alert(result.error || 'Failed to load night run');
      }
    } catch (err) {
      alert(`Failed to load night run: ${err.message}`);
    } finally {
      setAddingNightRuns(prev => ({ ...prev, [key]: false }));
    }
  }, [apiBaseUrl, capacityData, scheduleStartDate]);

  // Upload a manual schedule Excel (results Excel with OPT sheets)
  const handleUploadManual = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    e.target.value = '';

    setUploadingManual(true);
    try {
      // Count existing manual uploads to determine label index
      const existingManualCount = solutions.filter(
        s => s.id && s.id.startsWith('manual_upload')
      ).length;
      const manualIndex = existingManualCount + 1;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('manualIndex', String(manualIndex));
      formData.append('scheduleStartDate', scheduleStartDate || '');

      const response = await fetch(`${apiBaseUrl}/api/revenue-impact/upload-manual`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      if (result.success && result.solutions && result.solutions.length > 0) {
        const newSols = result.solutions;
        // Show warnings/errors as alerts
        for (const sol of newSols) {
          const msgs = [...(sol.errors || []), ...(sol.warnings || [])];
          if (msgs.length > 0) {
            alert(`${sol.label}:\n${msgs.join('\n')}`);
          }
        }
        setSolutions(prev => [...prev, ...newSols]);
      } else {
        alert(result.error || 'Failed to process manual schedule');
      }
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploadingManual(false);
    }
  }, [apiBaseUrl, solutions, scheduleStartDate]);

  // Export CSV
  const handleExportCSV = useCallback(() => {
    const lines = ['solution,day,jobs_completed,daily_net,accumulated_net\n'];
    for (const sol of solutions) {
      const accumulated = calculateAccumulated(sol.jobs || [], params);
      for (const point of accumulated) {
        const dailyNet = point.dailyJobs > 0 ? (point.accumulated - (accumulated[accumulated.indexOf(point) - 1]?.accumulated || 0)) : 0;
        lines.push(`${sol.label},${point.day},${point.dailyJobs},${dailyNet.toFixed(2)},${point.accumulated.toFixed(2)}\n`);
      }
    }
    const blob = new Blob(lines, { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'revenue_impact_comparison.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [solutions, params]);

  // Y-axis formatter
  const yAxisFormatter = useCallback((value) => formatCurrency(value), []);

  // Zoom state — mouse wheel to zoom X, drag rectangle zooms X+Y
  const [xDomain, setXDomain] = useState([undefined, undefined]);
  const [yDomain, setYDomain] = useState([undefined, undefined]);
  const chartContainerRef = useRef(null);

  // Mouse wheel zoom handler
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (!chartData || chartData.length === 0) return;
      e.preventDefault();

      const allDays = chartData.map(d => d.day);
      const minDay = allDays[0];
      const maxDay = allDays[allDays.length - 1];
      const fullRange = maxDay - minDay;
      // Allow zooming down to 5% of the full data range
      const minRange = Math.max(0.2, fullRange * 0.05);

      const curMin = xDomain[0] !== undefined ? xDomain[0] : minDay;
      const curMax = xDomain[1] !== undefined ? xDomain[1] : maxDay;
      const range = curMax - curMin;

      // Get mouse X position as fraction of container width
      const rect = container.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left - 60) / (rect.width - 90)));

      const zoomFactor = e.deltaY < 0 ? 0.75 : 1.33; // scroll up = zoom in
      const newRange = Math.max(minRange, range * zoomFactor);

      // Zoom around cursor position
      let newMin = curMin + frac * (range - newRange);
      let newMax = newMin + newRange;

      // Clamp to data bounds
      if (newMin < minDay) { newMin = minDay; newMax = newMin + newRange; }
      if (newMax > maxDay) { newMax = maxDay; newMin = Math.max(minDay, newMax - newRange); }

      // If fully zoomed out, reset to undefined
      if (newMin <= minDay && newMax >= maxDay) {
        setXDomain([undefined, undefined]);
      } else {
        // Use fractional values — no rounding — for smooth deep zoom
        setXDomain([parseFloat(newMin.toFixed(2)), parseFloat(newMax.toFixed(2))]);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [chartData, xDomain]);

  // Rectangle drag-zoom state (DOM-level for pixel-precise 2D selection)
  const [dragRect, setDragRect] = useState(null); // { startX, startY, currentX, currentY }
  const dragRef = useRef(null);

  // Compute the chart plot area bounds inside the container.
  // recharts margins: { top: 10, right: 30, left: 20, bottom: 10 } + YAxis label area
  const getChartBounds = useCallback(() => {
    const container = chartContainerRef.current;
    if (!container) return null;
    const w = container.clientWidth;
    const h = container.clientHeight;
    // Approximate plot area (left offset includes YAxis labels ~55px + margin 20)
    return { left: 75, right: w - 30, top: 10, bottom: h - 35 };
  }, []);

  // Convert pixel X (relative to container) → day value
  const pxToDay = useCallback((px) => {
    const bounds = getChartBounds();
    if (!bounds || !chartData || chartData.length === 0) return null;
    const chartW = bounds.right - bounds.left;
    if (chartW <= 0) return null;

    const allDays = chartData.map(d => d.day);
    const minDay = allDays[0];
    const maxDay = allDays[allDays.length - 1];
    const curMin = xDomain[0] !== undefined ? xDomain[0] : minDay;
    const curMax = xDomain[1] !== undefined ? xDomain[1] : maxDay;

    const frac = Math.max(0, Math.min(1, (px - bounds.left) / chartW));
    return curMin + frac * (curMax - curMin);
  }, [chartData, xDomain, getChartBounds]);

  // Convert pixel Y (relative to container) → dollar value
  // Y axis is inverted: top = max value, bottom = min value
  const pxToValue = useCallback((py) => {
    const bounds = getChartBounds();
    if (!bounds || !chartData || chartData.length === 0) return null;
    const chartH = bounds.bottom - bounds.top;
    if (chartH <= 0) return null;

    // Compute visible Y range from chart data
    let minVal = Infinity, maxVal = -Infinity;
    for (const d of chartData) {
      for (const key of Object.keys(d)) {
        if (key === 'day') continue;
        const v = d[key];
        if (v != null && typeof v === 'number') {
          if (v < minVal) minVal = v;
          if (v > maxVal) maxVal = v;
        }
      }
    }
    if (yDomain[0] !== undefined) minVal = yDomain[0];
    if (yDomain[1] !== undefined) maxVal = yDomain[1];
    // Add ~5% padding like recharts does
    const padding = (maxVal - minVal) * 0.05;
    const vMin = minVal - padding;
    const vMax = maxVal + padding;

    // Inverted: top of chart = max, bottom = min
    const frac = Math.max(0, Math.min(1, (py - bounds.top) / chartH));
    return vMax - frac * (vMax - vMin);
  }, [chartData, yDomain, getChartBounds]);

  // DOM-level mouse handlers for drag-to-zoom
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (e.target.closest('button, input, .ri-legend-custom, .ri-zoom-reset-btn')) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      dragRef.current = { startX: x, startY: y };
      setDragRect({ startX: x, startY: y, currentX: x, currentY: y });
      e.preventDefault();
    };

    const onMouseMove = (e) => {
      if (!dragRef.current) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setDragRect(prev => prev ? { ...prev, currentX: x, currentY: y } : null);
    };

    const onMouseUp = (e) => {
      if (!dragRef.current) return;
      const rect = container.getBoundingClientRect();
      const endX = e.clientX - rect.left;
      const endY = e.clientY - rect.top;
      const startX = dragRef.current.startX;
      const startY = dragRef.current.startY;
      dragRef.current = null;
      setDragRect(null);

      // Only zoom if dragged more than 10px in at least one direction
      const dx = Math.abs(endX - startX);
      const dy = Math.abs(endY - startY);
      if (dx < 10 && dy < 10) return;

      // X zoom
      if (dx >= 10) {
        const dayLeft = pxToDay(Math.min(startX, endX));
        const dayRight = pxToDay(Math.max(startX, endX));
        if (dayLeft != null && dayRight != null && dayRight > dayLeft) {
          setXDomain([parseFloat(dayLeft.toFixed(2)), parseFloat(dayRight.toFixed(2))]);
        }
      }

      // Y zoom
      if (dy >= 10) {
        const valTop = pxToValue(Math.min(startY, endY));    // top pixel = higher value
        const valBottom = pxToValue(Math.max(startY, endY)); // bottom pixel = lower value
        if (valTop != null && valBottom != null && valTop > valBottom) {
          setYDomain([Math.floor(valBottom), Math.ceil(valTop)]);
        }
      }
    };

    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [pxToDay, pxToValue]);

  const handleZoomReset = useCallback(() => {
    setXDomain([undefined, undefined]);
    setYDomain([undefined, undefined]);
  }, []);

  return createPortal(
    <div className="revenue-impact-overlay" onClick={onClose}>
      <div className="revenue-impact-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="ri-header">
          <h2>📊 Revenue Impact Comparison</h2>
          <button className="ri-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="ri-content" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="ri-loading">
              <div className="ri-spinner" />
              <span>Computing revenue metrics...</span>
            </div>
          </div>
        ) : error ? (
          <div className="ri-content" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="ri-error">
              <span style={{ fontSize: '48px' }}>⚠️</span>
              <span>{error}</span>
              <button className="ri-btn" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <>
            <div className="ri-content">
              {/* Chart Area */}
              <div className="ri-chart-area">
                <div className="ri-chart-container" ref={chartContainerRef}>
                  {(xDomain[0] !== undefined || yDomain[0] !== undefined) && (
                    <button
                      className="ri-zoom-reset-btn"
                      onClick={handleZoomReset}
                      title="Reset zoom"
                    >
                      🔍 Reset Zoom
                    </button>
                  )}
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="day"
                        domain={xDomain[0] !== undefined ? xDomain : ['dataMin', 'dataMax']}
                        allowDataOverflow={true}
                        type="number"
                        label={{ value: 'Day', position: 'insideBottom', offset: -5, style: { fill: '#6b7280', fontSize: 12 } }}
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                      />
                      <YAxis
                        tickFormatter={yAxisFormatter}
                        domain={yDomain[0] !== undefined ? yDomain : ['auto', 'auto']}
                        allowDataOverflow={true}
                        label={{ value: 'Accumulated Net Profit ($)', angle: -90, position: 'insideLeft', offset: -5, style: { fill: '#6b7280', fontSize: 12 } }}
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                      />
                      <Tooltip content={<CustomTooltip solutions={solutions} chartData={chartData} />} />
                      {solutions.map((sol, i) => {
                        const style = getLineStyle(i);
                        return (
                          <Line
                            key={sol.id}
                            type="monotone"
                            dataKey={sol.id}
                            name={sol.id}
                            stroke={style.color}
                            strokeWidth={2.5}
                            strokeDasharray={style.dash}
                            dot={{ r: 4, strokeWidth: 1.5, fill: '#fff' }}
                            activeDot={{ r: 6 }}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>

                  {/* Drag-to-zoom selection rectangle */}
                  {dragRect && (Math.abs(dragRect.currentX - dragRect.startX) > 4 || Math.abs(dragRect.currentY - dragRect.startY) > 4) && (
                    <div
                      className="ri-drag-selection"
                      style={{
                        left: Math.min(dragRect.startX, dragRect.currentX),
                        top: Math.min(dragRect.startY, dragRect.currentY),
                        width: Math.abs(dragRect.currentX - dragRect.startX),
                        height: Math.abs(dragRect.currentY - dragRect.startY),
                      }}
                    />
                  )}

                  {/* Editable Legend Overlay */}
                  <div className="ri-legend-custom">
                    {solutions.map((sol, i) => {
                      const style = getLineStyle(i);
                      const isEditing = editingLegendId === sol.id;
                      return (
                        <div
                          key={sol.id}
                          className="ri-legend-item"
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setLegendContextMenu({ solId: sol.id, x: e.clientX, y: e.clientY });
                          }}
                        >
                          <svg width="18" height="10" style={{ flexShrink: 0 }}>
                            <line
                              x1="0" y1="5" x2="18" y2="5"
                              stroke={style.color}
                              strokeWidth={2.5}
                              strokeDasharray={style.dash || undefined}
                            />
                          </svg>
                          {isEditing ? (
                            <input
                              className="ri-legend-edit-input"
                              autoFocus
                              defaultValue={sol.label}
                              onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v && v !== sol.label) handleLabelChange(sol.id, v);
                                setEditingLegendId(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.target.blur();
                                if (e.key === 'Escape') setEditingLegendId(null);
                              }}
                            />
                          ) : (
                            <span
                              className="ri-legend-label"
                              style={{ color: style.color }}
                              onClick={() => setEditingLegendId(sol.id)}
                              title="Click to rename"
                            >
                              {sol.label}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Legend right-click context menu */}
                  {legendContextMenu && (
                    <div
                      className="ri-legend-context-menu"
                      style={{ top: legendContextMenu.y, left: legendContextMenu.x }}
                    >
                      <button
                        className="ri-context-menu-item ri-context-menu-item-danger"
                        onClick={() => {
                          handleRemoveSolution(legendContextMenu.solId);
                          setLegendContextMenu(null);
                        }}
                      >
                        🗑 Remove from graph
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Slider Panel */}
              <div className="ri-slider-panel">
                <h3>Financial Parameters</h3>
                <p className="ri-panel-subtitle">Drag sliders to recalculate in real time</p>
                <div className="ri-panel-divider" />

                {SLIDER_CONFIG.map(cfg => (
                  <div key={cfg.key} className="ri-slider-group">
                    <div className="ri-slider-label-row">
                      <span className="ri-slider-name">{cfg.label}</span>
                      <span className="ri-slider-value">${params[cfg.key].toLocaleString()}</span>
                    </div>
                    <input
                      type="range"
                      className="ri-slider-input"
                      min={cfg.min}
                      max={cfg.max}
                      step={cfg.step}
                      value={params[cfg.key]}
                      onChange={(e) => handleParamChange(cfg.key, e.target.value)}
                    />
                  </div>
                ))}

                <div className="ri-panel-divider" />
                <div className="ri-panel-actions">
                  <button className="ri-btn" onClick={handleReset}>Reset</button>
                  <button className="ri-btn" onClick={handleExportCSV}>Export CSV</button>
                </div>
              </div>
            </div>

            {/* Solutions Section */}
            <div className="ri-solutions-section">
              <div className="ri-solutions-header">
                <h4>Solutions</h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="ri-btn ri-btn-primary"
                    onClick={() => manualFileInputRef.current?.click()}
                    disabled={uploadingManual}
                  >
                    {uploadingManual ? '⏳ Loading...' : '📂 Load Manual Schedule'}
                  </button>
                  <input
                    ref={manualFileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.xlsm"
                    style={{ display: 'none' }}
                    onChange={handleUploadManual}
                  />
                  <button className="ri-btn ri-btn-night" onClick={handleOpenNightRunsPicker}>
                    🌙 Load Night Runs
                  </button>
                  <button className="ri-btn ri-btn-primary" onClick={handleOpenAddPicker}>
                    + Add Solution
                  </button>
                </div>
              </div>
              <div className="ri-solutions-list">
                {solutions.map((sol, i) => {
                  const isLocked = sol.id === 'manual' || sol.id === 'optimized';
                  const style = getLineStyle(i);
                  return (
                    <div key={sol.id} className="ri-solution-card">
                      <div
                        className="ri-solution-color-dot"
                        style={{
                          background: style.dash ? 'transparent' : style.color,
                          border: style.dash ? `2px ${style.dash.includes(' ') ? 'dashed' : 'solid'} ${style.color}` : 'none',
                          ...(style.dash ? {} : { background: style.color }),
                        }}
                      />
                      <input
                        className="ri-solution-name-input"
                        value={sol.label}
                        onChange={(e) => handleLabelChange(sol.id, e.target.value)}
                        title="Click to rename"
                      />
                      <span className="ri-solution-total">
                        ${solutionTotals[sol.id]?.toLocaleString() || '0'}
                      </span>
                      <span className="ri-solution-job-count" title="Number of jobs in this solution">
                        ({sol.total_jobs || 0} jobs)
                      </span>
                      {isLocked ? (
                        <span style={{ fontSize: '12px', opacity: 0.4, padding: '0 4px' }}>🔒</span>
                      ) : (
                        <button
                          className="ri-solution-remove-btn"
                          onClick={() => handleRemoveSolution(sol.id)}
                          title="Remove solution"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Job count mismatch warning */}
              {(() => {
                const counts = solutions.map(s => s.total_jobs || 0).filter(c => c > 0);
                if (counts.length >= 2 && new Set(counts).size > 1) {
                  const min = Math.min(...counts);
                  const max = Math.max(...counts);
                  return (
                    <div className="ri-job-count-warning">
                      ⚠️ Job counts differ between solutions ({min} vs {max}).
                      The comparison may be misleading — ensure all solutions cover the same jobs.
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </>
        )}
      </div>

      {/* Night Runs Picker Modal */}
      {showNightRunsPicker && createPortal(
        <div className="ri-add-picker-overlay" onClick={() => setShowNightRunsPicker(false)}>
          <div className="ri-add-picker ri-nightruns-picker" onClick={(e) => e.stopPropagation()}>
            <h3>
              <span>🌙 Load Night Runs</span>
              <button
                className="ri-close-btn"
                onClick={() => setShowNightRunsPicker(false)}
                style={{ fontSize: '18px' }}
              >
                ✕
              </button>
            </h3>
            <div className="ri-add-picker-list">
              {loadingNightRuns ? (
                <div className="ri-loading" style={{ padding: '40px' }}>
                  <div className="ri-spinner" />
                  <span>Scanning night runs...</span>
                </div>
              ) : nightRunsDates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  No night run results found
                </div>
              ) : (
                nightRunsDates.map(dateEntry => {
                  const allLoaded = dateEntry.runs.every(r =>
                    solutions.some(s => s.id === `nightrun_${dateEntry.date}_${r.name}`)
                  );
                  const isLoading = addingNightRuns[dateEntry.date];
                  return (
                    <div key={dateEntry.date} className="ri-nightrun-date-group">
                      <div className="ri-nightrun-date-header">
                        <span className="ri-nightrun-date-label">📅 {dateEntry.date}</span>
                        <span className="ri-nightrun-run-count">{dateEntry.runs.length} run(s)</span>
                        <button
                          className="ri-btn ri-btn-sm ri-btn-night"
                          disabled={allLoaded || isLoading}
                          onClick={() => handleLoadNightRunDate(dateEntry)}
                        >
                          {isLoading ? '⏳ Loading...' : allLoaded ? '✓ All loaded' : 'Load All'}
                        </button>
                      </div>
                      <div className="ri-nightrun-runs">
                        {dateEntry.runs.map(run => {
                          const runKey = `${dateEntry.date}_${run.name}`;
                          const alreadyLoaded = solutions.some(
                            s => s.id === `nightrun_${dateEntry.date}_${run.name}`
                          );
                          const runLoading = addingNightRuns[runKey];
                          return (
                            <div
                              key={run.name}
                              className="ri-nightrun-run-item"
                              style={{
                                opacity: alreadyLoaded ? 0.5 : 1,
                                pointerEvents: alreadyLoaded || runLoading ? 'none' : 'auto',
                              }}
                              onClick={() => handleLoadSingleNightRun(dateEntry, run)}
                            >
                              <span className="ri-nightrun-run-name">{run.name}</span>
                              <span className="ri-nightrun-run-desc">
                                {run.description || 'No description'}
                              </span>
                              <span className="ri-nightrun-run-wcs">
                                {run.workCenters.join(', ')} — {run.fileCount} files
                              </span>
                              {alreadyLoaded && <span className="ri-nightrun-loaded-badge">✓</span>}
                              {runLoading && <span>⏳</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Solution Picker Modal */}
      {showAddPicker && createPortal(
        <div className="ri-add-picker-overlay" onClick={() => setShowAddPicker(false)}>
          <div className="ri-add-picker" onClick={(e) => e.stopPropagation()}>
            <h3>
              <span>Select an optimization run</span>
              <button
                className="ri-close-btn"
                onClick={() => setShowAddPicker(false)}
                style={{ fontSize: '18px' }}
              >
                ✕
              </button>
            </h3>
            <div className="ri-add-picker-list">
              {loadingGroups ? (
                <div className="ri-loading" style={{ padding: '40px' }}>
                  <div className="ri-spinner" />
                  <span>Loading results...</span>
                </div>
              ) : availableGroups.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  No optimization results found
                </div>
              ) : (
                availableGroups.map(group => {
                  const alreadyLoaded = solutions.some(
                    s => s.id === `run_${group.timestamp}`
                  );
                  return (
                    <div
                      key={group.timestamp}
                      className="ri-add-picker-item"
                      style={{
                        opacity: alreadyLoaded || addingId === group.timestamp ? 0.5 : 1,
                        pointerEvents: alreadyLoaded || addingId ? 'none' : 'auto'
                      }}
                      onClick={() => handleAddSolution(group)}
                    >
                      <div className="ri-add-picker-item-timestamp">
                        {group.timestamp.replace(/T/g, ' ').replace(/-/g, ':').substring(0, 19)}
                        {alreadyLoaded && ' (already loaded)'}
                        {addingId === group.timestamp && ' ⏳ Loading...'}
                      </div>
                      <div className="ri-add-picker-item-wcs">
                        {group.workCenters.join(', ')} — {group.files.length} files
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>,
    document.body
  );
};

export default RevenueImpactModal;
